import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';

import { LoadingService } from './core/services/loading-service/loading.service';
import { SEOService } from './core/services/seo-service/seo.service';
import { EnvironmentVariablesService } from './core/services/environment-variable-service/environment-variables.service';
import { AuthService } from './core/services/auth-service/auth.service';
import { CookieStorageService } from './core/services/cookie-storage-service/cookie-storage.service';
import { CustomIconService } from './core/services/custom-icon-service/custom-icon.service';
import { JsonLdService } from './shared/services/json-ld/json-ld.service';
import { AdCampaignService } from './shared/services/ad-campaign/ad-campaign.service';
import { GoogleAnalyticsService } from './core/services/google-analytics-service/google-analytics.service';
import { WINDOW } from './shared/services/window/window.service';

import { environment } from '../environments/environment';
import { ExposedEnvironmentVariable } from '../environments/environment-variables';
import { GlobalConstants } from './global/global.constants';
import { CookieAcceptanceStatusEnum } from './global/global.enums';
import { FacebookPixelService } from './core/services/facebook-pixel-service/facebook-pixel.service';

export const env = environment;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading = false;
  loadingStatus = false;
  showCookieDisclaimer = false;
  private _destroy$ = new Subject<void>();
  private previousUrl: string;

  constructor(
    public loadingService: LoadingService,
    public seoService: SEOService,
    private activatedRoute: ActivatedRoute,
    private environmentVariablesService: EnvironmentVariablesService,
    private authService: AuthService,
    @Inject(WINDOW) private window: Window,
    @Inject(PLATFORM_ID) private platformID: object,
    private cookieStorageService: CookieStorageService,
    private router: Router,
    private iconService: CustomIconService,
    private jsonLdService: JsonLdService,
    private adCampaignService: AdCampaignService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private facebookPixelService: FacebookPixelService
  ) {}

  ngOnInit(): void {
    this.initializeDataCollection();

    this.iconService.importIcons();
    this.subscribeToRouterEvents();
    this.subscribeToLoadingStates();
    if (isPlatformBrowser(this.platformID)) {
      this.handleCookieDialog();
    }
  }

  cookieAccepted(): void {
    this.showCookieDisclaimer = false;
  }

  private subscribeToRouterEvents(): void {
    this.router.events.pipe(takeUntil(this._destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.handleNavigation(event);
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => this.updateSEO(data));
  }

  private subscribeToLoadingStates(): void {
    this.loadingService
      .getLoadingSubject()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this.isLoading = result;
      });

    this.authService
      .getAuthLoadingAsObservable()
      .pipe(takeUntil(this._destroy$))
      .subscribe((loadingStatus) => {
        this.loadingStatus = loadingStatus;
      });
  }

  private handleNavigation(event: NavigationEnd): void {
    if (!isPlatformBrowser(this.platformID)) return;

    this.activatedRoute.queryParams.pipe(take(1)).subscribe((queryParams) => {
      const shouldScroll =
        (!(this.previousUrl?.includes('/keszulekek/') && this.router.url.includes('/keszulekek/')) ||
          (this.previousUrl?.includes('/keszulekek/') && !this.router.url.includes('/keszulekek/'))) &&
        queryParams['scroll'] !== 'false';

      if (shouldScroll) {
        this.window.scroll(0, 0);
      }

      this.previousUrl = this.router.url;
      this.adCampaignService.setAdCampaign(queryParams[GlobalConstants.adCampaignIdentifier]);
    });
    this.trackPageViews(event);
  }

  private trackPageViews(event: NavigationEnd) {
    this.googleAnalyticsService.trackPageView(event.urlAfterRedirects);
    this.facebookPixelService.trackPageView(event.urlAfterRedirects);
  }

  private updateSEO(data: any): void {
    if (data.setDefaultJsonLd) {
      this.jsonLdService.addOrUpdateStructuredData(JsonLdService.orgSchema());
    }

    if (data.setMetaByDefault) {
      this.seoService.updateTitle(data.meta_title);
      this.seoService.updateDescription(data.meta_description);
      this.seoService.updateMetaTag('keywords', data.meta_keywords);
      this.seoService.updateMetaTag('author', data.meta_author);
      this.seoService.updateMetaTag('og:title', data.og_title);
      this.seoService.updateMetaTag('og:description', data.og_description);
      this.seoService.updateMetaTag(
        'og:image',
        `${this.environmentVariablesService.getVar(ExposedEnvironmentVariable.NETFONE_URL)}/assets/og/${data.og_image}`
      );
      this.seoService.updateMetaTag('og:image:type', data.og_image_type);
      this.seoService.updateMetaTag('og:image:width', data.og_image_width);
      this.seoService.updateMetaTag('og:image:height', data.og_image_height);
      this.seoService.updateMetaTag(
        'og:url',
        `${this.environmentVariablesService.getVar(ExposedEnvironmentVariable.NETFONE_URL)}/${this.router.url}`
      );
    }
  }

  private handleCookieDialog(): void {
    const acceptedType = this.cookieStorageService.getItem(GlobalConstants.cookieAcceptedKey);
    if (!acceptedType) {
      this.showCookieDisclaimer = true;
      return;
    }
    if (acceptedType === CookieAcceptanceStatusEnum.ACCEPTED_ALL) {
      this.enableDacaCollection();
    }
  }

  private enableDacaCollection() {
    this.googleAnalyticsService.enableAnalyticsEvents();
    this.facebookPixelService.enablePixelEvents();
  }

  private initializeDataCollection() {
    this.googleAnalyticsService.initializeOnStartup();
    this.facebookPixelService.initializeOnStartup();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
